import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal, Type } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { FieldArrayTypeConfig, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { Button } from '@openng/optimus-ui/button';
import { TableModule } from '@openng/optimus-ui/table';
import { LayoutService } from '../../../layout/services/layout.service';
import { FieldWrapperProps } from '../../wrappers/field-wrapper/field-wrapper.component';
import { BaseFieldArrayType } from '../base-field-array-type';

interface TableProps extends FieldWrapperProps {
  columns?: { key: string; header?: string; class?: string }[];
  showIndex?: boolean;
  stack?: boolean;

  addText?: string;
  addTextValue?: string;
  noDataText?: string;

  initialCount?: number;
  maxCount?: number;
  minCount?: number;

  scrollHeight?: string;
  is_resizable?: boolean;

  canAdd?: (field: FormlyFieldConfig<TableProps>) => boolean;
  canRemove?: (field: FormlyFieldConfig<TableProps>, index: number) => boolean;
}

export interface TableFieldConfig extends FormlyFieldConfig<TableProps> {
  type?: 'table' | Type<TableTypeComponent>;
}

@Component({
  selector: 'lib-formly-table-type',
  templateUrl: './table-type.component.html',
  styleUrl: './table-type.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [TableModule, FormlyModule, NgClass, Button, TranslocoDirective]
})
export class TableTypeComponent extends BaseFieldArrayType<FieldArrayTypeConfig<TableProps>> {
  protected readonly layoutService = inject(LayoutService);

  // Max height in px the user dragged the grip to; replaces props.scrollHeight
  readonly resizedHeight = signal<number | undefined>(undefined);
  private resizeStart?: { y: number; height: number };

  startResize(event: PointerEvent) {
    if (event.button !== 0) {
      return;
    }
    const grip = event.currentTarget as HTMLElement;
    grip.setPointerCapture(event.pointerId);
    this.resizeStart = { y: event.clientY, height: this.tableHeight(grip) };
    event.preventDefault();
  }

  resize(event: PointerEvent) {
    if (this.resizeStart) {
      this.setResizedHeight(this.resizeStart.height + event.clientY - this.resizeStart.y);
    }
  }

  stopResize() {
    this.resizeStart = undefined;
  }

  resizeBy(event: Event, delta: number) {
    this.setResizedHeight(this.tableHeight(event.currentTarget as HTMLElement) + delta);
    event.preventDefault();
  }

  private setResizedHeight(height: number) {
    this.resizedHeight.set(Math.max(100, height));
  }

  // The grip sits right after the p-table element
  private tableHeight(grip: HTMLElement): number {
    return grip.previousElementSibling?.getBoundingClientRect().height ?? 0;
  }

  getField(field: FormlyFieldConfig, column: any): FormlyFieldConfig | undefined {
    return field.fieldGroup?.find(f => f.key === column.key);
  }
}
